import { AxiosError } from 'axios'
import { useCallback, useMemo } from 'react'

import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query'

import { InvoiceFullProductData } from '@/interfaces/billing'

import APIServices from '../../api/APIServices'

export interface InvoiceProductsByAreaResponse {
  items: InvoiceFullProductData[]
  totalItems: number
  currentPage: number
  totalPages: number
}

export interface InvoiceProductsByAreaQueryParams {
  areaId?: number
  search?: string
  salesCategoryId?: number
  page?: number
  per_page?: number
  priceSystemId?: number
}

// Create a unique, descriptive key for this query
const INVOICE_PRODUCTS_BY_AREA_QUERY_KEY = 'invoice-products-by-area'

/**
 * Fetch invoice products from the API
 * Separating API call logic from the hook for better testability
 */
const fetchInvoiceProductsByArea = async (
  params?: InvoiceProductsByAreaQueryParams
): Promise<InvoiceProductsByAreaResponse> => {
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([_, value]) => value != null)
  )

  const response = await APIServices.get(
    `/invoice/products/${params?.areaId}/find`,
    {
      params: cleanParams,
    }
  )
  return response.data
}

/**
 * Custom hook for fetching invoice products by area with infinite loading
 *
 * @param params - Query parameters for filtering invoice products by area
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useInvoiceProductsByAreaQuery = ({
  params,
  options,
  businessId,
}: {
  params: InvoiceProductsByAreaQueryParams
  options?: Partial<
    UseInfiniteQueryOptions<InvoiceProductsByAreaResponse, AxiosError>
  >
  businessId: number
}) => {
  // Normalize params with defaults
  const normalizedParams: InvoiceProductsByAreaQueryParams = {
    page: 1,
    per_page: 20, // Default page size for infinite loading
    ...params,
  }

  // Infinite query hook
  const queryResult = useInfiniteQuery({
    // Create a structured query key that includes all parameters except page
    queryKey: [
      INVOICE_PRODUCTS_BY_AREA_QUERY_KEY,
      { ...normalizedParams, page: undefined },
      { businessId },
    ],

    // Query function that receives pageParam from context
    queryFn: ({ pageParam = 1 }) =>
      fetchInvoiceProductsByArea({
        ...normalizedParams,
        page: pageParam as number,
      }),

    // Function to get the next page parameter
    getNextPageParam: (
      lastPage: InvoiceProductsByAreaResponse
    ): number | undefined => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1
      }
      return undefined
    },

    // Get initial page param
    initialPageParam: 1,

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options (exclude conflicting types)
    ...options,
  })

  // Type the infinite data properly
  const infiniteData = queryResult.data as
    | InfiniteData<InvoiceProductsByAreaResponse>
    | undefined

  // Flatten all products from all pages
  const allProducts = useMemo(() => {
    if (!infiniteData?.pages) return []
    return infiniteData.pages.flatMap(
      (page: InvoiceProductsByAreaResponse) => page.items
    )
  }, [infiniteData?.pages])

  // Get total items from the first page
  const totalItems = useMemo(() => {
    return infiniteData?.pages?.[0]?.totalItems || 0
  }, [infiniteData?.pages])

  // Get total pages from the first page
  const totalPages = useMemo(() => {
    return infiniteData?.pages?.[0]?.totalPages || 0
  }, [infiniteData?.pages])

  // Get current loaded pages count
  const loadedPages = useMemo(() => {
    return infiniteData?.pages?.length || 0
  }, [infiniteData?.pages])

  // Helper functions for infinite loading
  const hasNextPage = useCallback(() => {
    return queryResult.hasNextPage
  }, [queryResult.hasNextPage])

  const canLoadMore = useCallback(() => {
    return hasNextPage() && !queryResult.isFetchingNextPage
  }, [hasNextPage, queryResult.isFetchingNextPage])

  // Function to find a product by ID, loading more pages if necessary
  const findProductById = useCallback(
    async (productId: number): Promise<InvoiceFullProductData | null> => {
      // First check in already loaded products
      let product = allProducts.find(
        (p: InvoiceFullProductData) => p.id === productId
      )
      if (product) {
        return product
      }

      // Safety mechanisms to prevent infinite loops
      const MAX_ATTEMPTS = 10 // Maximum number of page loads
      const TIMEOUT_MS = 15000 // 15 second timeout
      let attempts = 0
      const startTime = Date.now()

      // If not found and we can load more, keep loading until found or limits reached
      while (attempts < MAX_ATTEMPTS && Date.now() - startTime < TIMEOUT_MS) {
        // Check current state fresh each iteration
        const currentHasNextPage = queryResult.hasNextPage
        const currentIsFetching = queryResult.isFetchingNextPage

        if (!currentHasNextPage || currentIsFetching) {
          break
        }

        attempts++

        try {
          // Store current page count before fetch
          const pageCountBefore =
            (
              queryResult.data as
                | InfiniteData<InvoiceProductsByAreaResponse>
                | undefined
            )?.pages?.length || 0

          // Fetch next page
          await queryResult.fetchNextPage()

          // Wait a bit for state to update
          await new Promise((resolve) => setTimeout(resolve, 100))

          // Get the most recent data from the query result
          const currentInfiniteData = queryResult.data as
            | InfiniteData<InvoiceProductsByAreaResponse>
            | undefined
          const pageCountAfter = currentInfiniteData?.pages?.length || 0

          // Check if we have data and new pages were actually loaded
          if (!currentInfiniteData?.pages) {
            break
          }

          if (pageCountAfter <= pageCountBefore) {
            break
          }

          // Get all products from all loaded pages
          const updatedProducts = currentInfiniteData.pages.flatMap(
            (page: InvoiceProductsByAreaResponse) => page.items
          )

          // Search for product in updated data
          product = updatedProducts.find(
            (p: InvoiceFullProductData) => p.id === productId
          )

          if (product) {
            return product
          }

          // Small delay to prevent overwhelming the system
          await new Promise((resolve) => setTimeout(resolve, 100))
        } catch (error) {
          console.error(
            `❌ [findProductById] Error loading page ${attempts}:`,
            error
          )
          break
        }
      }

      // Final check in allProducts after all attempts

      const finalProduct = allProducts.find(
        (p: InvoiceFullProductData) => p.id === productId
      )
      if (finalProduct) {
        return finalProduct
      }

      return null
    },
    [
      allProducts,
      queryResult.hasNextPage,
      queryResult.isFetchingNextPage,
      queryResult.fetchNextPage,
      queryResult.data,
    ]
  )

  // Function to ensure a product is loaded (for focusing)
  const ensureProductLoaded = useCallback(
    async (productId: number): Promise<boolean> => {
      try {
        const product = await findProductById(productId)
        const found = !!product

        return found
      } catch (error) {
        console.error(
          `❌ Error ensuring product ${productId} is loaded:`,
          error
        )
        return false
      }
    },
    [findProductById]
  )

  // Helper functions for pagination metadata
  const getCurrentPage = useCallback(() => {
    return loadedPages
  }, [loadedPages])

  const getPageSize = useCallback(() => {
    return normalizedParams.per_page || 20
  }, [normalizedParams.per_page])

  const getLoadedItemsCount = useCallback(() => {
    return allProducts.length
  }, [allProducts.length])

  const isAllDataLoaded = useCallback(() => {
    return !hasNextPage()
  }, [hasNextPage])

  return {
    // Flattened products from all pages
    products: allProducts,
    totalItems,
    totalPages,
    loadedPages,

    // Loading states
    isLoadingProducts: queryResult.isLoading,
    isErrorProducts: queryResult.isError,
    isFetchingNextPage: queryResult.isFetchingNextPage,

    // Actions
    refetchProducts: queryResult.refetch,
    fetchNextPage: queryResult.fetchNextPage,

    // Infinite loading helpers
    hasNextPage: hasNextPage(),
    canLoadMore: canLoadMore(),
    isAllDataLoaded: isAllDataLoaded(),

    // Product finding utilities
    findProductById,
    ensureProductLoaded,

    // Pagination metadata
    pagination: {
      currentPage: getCurrentPage(),
      pageSize: getPageSize(),
      loadedItemsCount: getLoadedItemsCount(),
      totalPages,
      totalItems,
      hasNextPage: hasNextPage(),
      canLoadMore: canLoadMore(),
      isAllDataLoaded: isAllDataLoaded(),
      params: normalizedParams,
    },

    // Raw query result for advanced usage
    queryResult,
  }
}

/**
 * Helper function to get the invoice products by area query key
 * Useful for cache invalidation or prefetching
 */
export const getInvoiceProductsByAreaQueryKey = () => [
  INVOICE_PRODUCTS_BY_AREA_QUERY_KEY,
]
